import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { useAppSelector, useFormDraft } from "@/shared/hooks";
import { Button, Input, Toast, PhoneInput } from "@/shared/ui";
import { buildPartialPayload, hasAnyValue } from "../partialSave";
import { useGetContactsQuery, useUpdateContactsMutation } from "./api";
import ContactsFormSkeleton from "./ContactsFormSkeleton";
import { type ContactsFormValues, contactsSchema } from "./validation";

interface Props {
  token: string;
  onNext: () => void;
  isChief?: boolean;
}

export default function ContactsForm({ token, onNext, isChief }: Props) {
  const { t } = useTranslation("ApplyApplicationPage");

  const isCompleted = useAppSelector((s) => s.completeness.data?.is_complete);
  const { data, isLoading, error } = useGetContactsQuery(token);
  const [updateContacts, { isLoading: isSaving }] = useUpdateContactsMutation();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [toast, setToast] = useState<{
    text: string;
    color: "positive" | "negative" | "notice" | "grey";
  } | null>(null);

  const methods = useForm<ContactsFormValues>({
    resolver: zodResolver(contactsSchema),
    defaultValues: {
      email: "",
      phone: "",
      phone_additional: "",
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const { applyDraft, hasValidDraft, clearDraft } = useFormDraft<ContactsFormValues>({
    key: `onboarding:contacts:${token}`,
    form: methods,
    schemaVersion: "v1",
  });

  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (data && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      reset({
        email: data.email ?? "",
        phone: data.phone ?? "",
        phone_additional: data.phone_additional ?? "",
      });
      if (hasValidDraft) applyDraft();
    } else if (!data && hasValidDraft) {
      applyDraft();
    }
  }, [data, reset, hasValidDraft, applyDraft]);

  const isNotFound = error && typeof error === "object" && "status" in error && error.status === 404;

  async function handleSave() {
    if (isSaving) return;
    const values = getValues();
    const payload = buildPartialPayload(values);
    if (!hasAnyValue(payload)) return;

    try {
      setIsSavingDraft(true);
      await updateContacts({ token, body: payload }).unwrap();
      setToast({ color: "positive", text: "contacts.toast.success" });
    } catch {
      setToast({ color: "negative", text: "contacts.toast.error" });
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function onSubmit(values: ContactsFormValues) {
    if (isSaving) return;

    try {
      await updateContacts({
        token,
        body: {
          ...values,
          phone_additional: values.phone_additional ?? "",
        },
      }).unwrap();

      clearDraft();
      setToast({
        color: "positive",
        text: "contacts.toast.success",
      });
      onNext();
    } catch {
      setToast({
        color: "negative",
        text: "contacts.toast.error",
      });
    }
  }

  if (isLoading && !data && !isNotFound) return <ContactsFormSkeleton />;

  return (
    <form className="flex flex-col justify-between min-h-[640px] gap-7" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-7">
        <h2 className="text-display-2xs content-base-primary">{t("contacts.title")}</h2>

        <div className="flex flex-col gap-4">
          <Input
            label={t("contacts.email.label")}
            hasRequiredLabel
            placeholder={t("contacts.email.placeholder")}
            {...register("email")}
            disabled={isChief && !!data?.email}
            error={errors.email?.message ? t(errors.email.message) : undefined}
          />

          <PhoneInput
            label={t("contacts.phone.label")}
            hasRequiredLabel
            value={watch("phone")}
            onChange={(value) => setValue("phone", value, { shouldDirty: true })}
            disabled={isChief && !!data?.phone}
            error={errors.phone?.message ? t(errors.phone.message) : undefined}
          />

          <PhoneInput
            label={t("contacts.phone_additional.label")}
            value={watch("phone_additional")}
            onChange={(value) => setValue("phone_additional", value, { shouldDirty: true })}
            error={errors.phone_additional?.message ? t(errors.phone_additional.message) : undefined}
          />
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {toast && (
          <Toast
            key={`${toast.color}-${toast.text}`}
            color={toast.color}
            text={t(toast.text)}
            onClose={() => setToast(null)}
          />
        )}

        <Button variant="secondary" size="lg" type="button" onClick={handleSave} disabled={isSaving}>
          {isSavingDraft ? t("contacts.saving") : t("contacts.save")}
        </Button>

        <Button variant="primary" size="lg" type="submit" disabled={isSaving}>
          {isSaving && !isSavingDraft ? t("contacts.saving") : t("contacts.next_section")}
          {(!isSaving || isSavingDraft) && (
            <span className="text-white">
              <ArrowRightIcon />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
